import { FC } from 'react'

import { Box, Divider, Text, Title, WizardStep } from '@factorialco/deprecated-design-system'
import { BenefitsHealthcareEnrollmentPlanFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { HealthcareEnrollmentData } from 'modules/benefits/components/benefitModals/HealthcareEnrollmentWizard/types'

import { SummaryRow, SummarySection } from './components/Summary'

interface Props {
  healthcarePlanData: BenefitsHealthcareEnrollmentPlanFragment
}

const PlanOverviewStep: FC<Props> = ({ healthcarePlanData }) => {
  const {
    employee,
    access: { locale },
  } = useCurrentSession()

  const { planData } = { planData: healthcarePlanData }
  // Get currency from employee's legal entity, fallback to EUR
  const currency = employee?.legalEntity?.currency ?? 'EUR'

  const formatCurrency = (cents: number) => {
    return MoneyWithCurrency({
      amount: cents,
      currency,
      locale,
    })
  }

  const formatWaitingPeriod = (months: number) => {
    if (months === 0)
      return i18n.t(
        'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.waiting_period.no_waiting_period'
      )
    return i18n.tp(
      'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.waiting_period',
      { count: months }
    )
  }

  return (
    <Box flexDirection="column" gap="s24" width="s400">
      <Box flexDirection="column" gap="s8">
        <Title
          variant="500"
          title={i18n.t('benefits.healthcare.enrollment_wizard.plan_overview_step.title')}
          description={i18n.t(
            'benefits.healthcare.enrollment_wizard.plan_overview_step.description'
          )}
        />
      </Box>

      <Box flexDirection="column" gap="s16">
        <Box flexDirection="column" gap="s8">
          <SummarySection title={i18n.t('benefits.healthcare.plan_wizard.summary_step.plan.label')}>
            <Text.Normal>
              {planData.providerName} {planData.planName}
            </Text.Normal>
          </SummarySection>
        </Box>

        <Divider />

        <Box flexDirection="column" gap="s8">
          <SummarySection
            title={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.basic_information.renewal_date.label'
            )}
          >
            <Text.Normal>{convertDate(planData.renewalDate, 'backend', 'ui')}</Text.Normal>
          </SummarySection>
        </Box>

        <Divider />

        <Box flexDirection="column" gap="s16">
          <SummarySection
            title={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.title'
            )}
          >
            {/* Waiting period */}
            <SummaryRow
              label={i18n.t(
                'benefits.healthcare.enrollment_wizard.plan_overview_step.enrollment_conditions.waiting_period'
              )}
              value={formatWaitingPeriod(planData.waitingPeriodMonths)}
            />

            {/* Pre-existing conditions */}
            <SummaryRow
              label={i18n.t(
                'benefits.healthcare.enrollment_wizard.plan_overview_step.enrollment_conditions.pre_existing_conditions.label'
              )}
              value={
                planData.allowPreExistingConditions
                  ? i18n.t(
                      'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.pre_existing_conditions.allowed'
                    )
                  : i18n.t(
                      'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.pre_existing_conditions.not_allowed'
                    )
              }
            />
          </SummarySection>
        </Box>

        <Divider />

        <Box flexDirection="column" gap="s16">
          <Text.Normal weight="semibold" size="300">
            {i18n.t('benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.label')}
          </Text.Normal>
          <Box flexDirection="column" gap="s16">
            {/* Employee cost */}
            <SummarySection
              title={i18n.t(
                'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.employee.label'
              )}
              small
            >
              <SummaryRow
                label={i18n.t(
                  'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.employee.total_plan_cost.label'
                )}
                value={formatCurrency(planData.employeeCostCents || 0)}
              />
              <SummaryRow
                label={i18n.t(
                  'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.employee.paid_by_company.label'
                )}
                value={formatCurrency(planData.coveredEmployeeCostCents || 0)}
              />
              <SummaryRow
                label={i18n.t(
                  'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.employee.paid_by_employee.label'
                )}
                value={formatCurrency(
                  (planData.employeeCostCents || 0) - (planData.coveredEmployeeCostCents || 0)
                )}
              />
            </SummarySection>

            {/* Dependent costs, if applicable */}
            {planData.dependentCostCents && (
              <>
                <SummarySection
                  title={i18n.t(
                    'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.dependent.label'
                  )}
                  small
                >
                  <SummaryRow
                    label={i18n.t(
                      'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.dependent.total_plan_cost.label'
                    )}
                    value={formatCurrency(planData.dependentCostCents || 0)}
                  />
                  <SummaryRow
                    label={i18n.t(
                      'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.dependent.paid_by_company.label'
                    )}
                    value={formatCurrency(planData.coveredDependentCostCents || 0)}
                  />
                  <SummaryRow
                    label={i18n.t(
                      'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.dependent.paid_by_employee.label'
                    )}
                    value={formatCurrency(
                      (planData.dependentCostCents || 0) - (planData.coveredDependentCostCents || 0)
                    )}
                  />
                </SummarySection>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

// Define step as factory function following the pattern from existing wizards
export const planOverviewStep = (): WizardStep<HealthcareEnrollmentData> => ({
  title: i18n.t('benefits.healthcare.enrollment_wizard.plan_overview_step.side_title'),
  render: (props) => <PlanOverviewStep healthcarePlanData={props.data.healthcarePlanData} />,
  nextText: i18n.t('common.continue'),
  isCompleted: () => true, // This step is always completed as it's just displaying info
})
